'use client';

import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useRef,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';

import { useLocalStorage } from '../hooks/useLocalStorage';
import { MOSACIKING } from '@/constants/enums/orderNew';

import { calculateMergeItemPrice } from '@/network/serverApis/existing';
import customToaster from '@/components/toast/CustomToaster';
import { CartItemCardData } from '@/components/cart/components/cartInterfaces';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import Polygon from '@arcgis/core/geometry/Polygon';
import { v4 as uuid } from 'uuid';
import orderType from '@/constants/enums/orderType';

import moment from 'moment';
import { emitMixpanelEvents } from '@/components/sider/sider.utils';
import { MixpanelEventHub } from '@/services/eventEnums';
import { mixPanelDateFormat } from '@/lib/utils';
import K from '@/constants/constants';
type ShoppingCartProviderProps = {
  children: ReactNode;
};

export type CartItem = {
  id: any;
  quantity: number;
  type: string;
  cardData: CartItemCardData;
  overlap?: MOSACIKING;
  overlapData?: CartItemCardData[];
  overlapMap?: { [key: string]: { [key: string]: boolean } };
  overlapResize?: { [key: string]: boolean };
  overlapError?: { map: boolean; resize: boolean };
  removePreview?: Function;
};

interface mergeItem {
  id?: boolean;
}

export interface taskingMapLegendsInterface {
  assuredTasking: boolean;
  droneTasking: boolean;
}
type IShoppingCartContext = {
  openCart: () => void;
  closeCart: () => void;
  getItemQuantity: (id: string) => number;
  increaseCartQuantity: (id: any, type: string, cardData: any, removePreviewCb: Function) => void;
  decreaseCartQuantity: (id: number) => void;
  updateCart: (cardData: any, fromOverlap?: Boolean, cartItemId?: string) => void;
  updateWholeCart: (cardData: any) => void;
  removeFromCart: (id: number | string) => void;
  resetCart: (items?: any, dontResetTax?: any) => void;
  AddSecondaryMergeItemCart: (id: string, cardData: any, coordinates: any) => Promise<void>;
  AddPrimaryMergeItemCart: (id: string, type: string, cardData: any) => void;
  removeSelectionItemCart: (id: string) => void;
  restoreCart: (items: any[]) => void;
  updateMultiProductItem: (
    cartId: string,
    updateCartData: {
      overlapData: CartItemCardData[];
      overlapMap: any;
      overlapError: { map: boolean; resize: boolean };
    },
    addCartData?: CartItem
  ) => boolean;
  confirmMergeItemCart: () => void;
  cartQuantity: number;
  cartItems: CartItem[];
  isNew: any;
  archiveUiElementsRef: any;
  taskingMapLegends: {
    assuredTasking: boolean;
    droneTasking: boolean;
  };
  setTaskingMapLegends: Dispatch<React.SetStateAction<taskingMapLegendsInterface>>;
  taskingUiElementsRef: any;
  setIsNew: any;
  drawer: any;
  setDrawer: any;
  isOpen: any;
  setIsOpen: any;
  openCreateAccountDrawer: any;
  setFeasibilityRequestData: any;
  setOpenCreateAccountDrawer: any;
  orderNewDataType: any;
  orderNewIsGuaranteedCapture: any;
  openPreviewInfoPopup: any;
  setOpenPreviewInfoPopup: any;
  archiveFilters: any;
  setAssuredTaskingDialog: any;
  assuredTaskingDialog: { minimize: boolean; show: boolean; maximize: boolean };
  setArchiveFilters: any;
  tax: any;
  setTax: any;
  aoiHeightWidth: { height: number | null; width: number | null };
  setAoiHeightWidth: Dispatch<SetStateAction<{ height: number | null; width: number | null }>>;
  feasibilityData: any;
  feasibilityRequestData: any;
  tabValue: any;
  mixpanelData: any;
  setMixpanelData: any;
  updateCartItem: (cartItem: CartItem) => void;
  mergeItemRef: React.MutableRefObject<any>; //this is object and used in local storage
  mergeOverlapRef: React.MutableRefObject<boolean>;
  multiProductCartItemRef: any;
  disableSrcRef: React.MutableRefObject<any>;
  disableBandsRef: React.MutableRefObject<any>;
  disableConstellationRef: React.MutableRefObject<any>;
  mergeOverlapPrimaryItemRef: React.MutableRefObject<boolean>; // this is to check if primary item is added or nots
  multiProduct: boolean;
  setMultiProduct: any;
  removeAndHighlightLayerCbRef: React.MutableRefObject<any>;
};

const ShoppingCartContext = createContext({} as IShoppingCartContext);

export function useShoppingCart() {
  return useContext(ShoppingCartContext);
}

let getPrimaryPolygonInterSectPercentage: any = null;

export function ShoppingCartProvider({ children }: ShoppingCartProviderProps) {
  // local states for managing the cart
  const [isOpen, setIsOpen] = useState(false);
  const [openCreateAccountDrawer, setOpenCreateAccountDrawer] = useState(false);
  const feasibilityData = useRef<any>(null);

  const [assuredTaskingDialog, setAssuredTaskingDialog] = useState<{
    minimize: boolean;
    show: boolean;
    maximize: boolean;
  }>({
    minimize: false,
    show: false,
    maximize: false,
  });

  const [taskingMapLegends, setTaskingMapLegends] = useState<taskingMapLegendsInterface>({
    assuredTasking: false,
    droneTasking: false,
  });
  const [aoiHeightWidth, setAoiHeightWidth] = useState<{
    height: number | null;
    width: number | null;
  }>({ height: null, width: null });

  // const feasibilityRequestData = useRef<any>(null);
  const [archiveFilters, setArchiveFilters] = useState<any>(null);
  const mergeItemRef = useRef<any>({});
  const mergeOverlapRef = useRef<boolean>(false);
  const mergeOverlapPrimaryItemRef = useRef<boolean>(false);
  const disableSrcRef = useRef('');
  const orderNewDataType = useRef('optical');
  // True while the active tasking form is configured for a point-based
  // guaranteed-capture flow (currently SkySat Assured). Lets the on-map area
  // label show "Guaranteed Capture Area" for an optical source.
  const orderNewIsGuaranteedCapture = useRef(false);
  const tabValue = useRef('1');
  const disableBandsRef = useRef('');
  const disableConstellationRef = useRef('');
  const [cartItems, setCartItems] = useLocalStorage('shopping-cart', []);
  const [feasibilityRequestData, setFeasibilityRequestData] = useLocalStorage(
    'feasibility-data',
    null
  );
  const [openPreviewInfoPopup, setOpenPreviewInfoPopup] = useState(false);
  const archiveUiElementsRef = useRef<any>(null);
  const taskingUiElementsRef = useRef<any>(null);

  //multi product states
  const [multiProduct, setMultiProduct] = useState<boolean>(false);
  const multiProductCartItemRef = useRef<CartItem>({} as CartItem);

  const [isNew, setIsNew] = useState('1');
  const [drawer, setDrawer] = useState(false);
  const [tax, setTax] = useState(0);
  const [mixpanelData, setMixpanelData] = useState<any>();

  //remove layer refs
  const removeAndHighlightLayerCbRef = useRef<any>();

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);

  useEffect(() => {
    //importing map library here due to resize observer error
    async function importMapLibrary() {
      const { getPrimaryPolygonInterSectPercentage: getInterSectPercentage } = await import(
        '@/context/MapContext/mapUtils'
      );
      getPrimaryPolygonInterSectPercentage = getInterSectPercentage;
    }
    importMapLibrary();
  }, []);

  // Get the cart quantity
  const cartQuantity = cartItems?.reduce((quantity, item) => item.quantity + quantity, 0) || 0;

  const checkForEmptyCardData = async () => {
    // find any empty cardData
    const anyEmptyCardData = cartItems?.filter((item: any) => {
      return item?.type === orderType.NEW && !item?.cardData?.id;
    });
    if (anyEmptyCardData?.length > 0) {
      // filter orders that dont have empty cardData
      const filterEmptyCardItems = cartItems?.filter((item: any) => {
        if (item?.type === orderType.NEW && item?.cardData?.id) {
          return item;
        } else if (item?.type === orderType.EXISTING) {
          return item;
        }
      });

      // reset the cart according to cardData
      if (filterEmptyCardItems?.length > 0) {
        resetCart(filterEmptyCardItems, true);
      } else {
        resetCart();
      }
    }
  };

  // remove any empty cardData
  checkForEmptyCardData();

  // Get the total quantity of items
  function getItemQuantity(id: string) {
    return cartItems.find(item => item.id === id)?.quantity || 0;
  }

  // Add to Cart
  function increaseCartQuantity(id: any, type: string, cardData: any, removePreviewCb: Function) {
    setCartItems(currItems => {
      if (currItems.find(item => item.id === id) == null) {
        return [
          ...currItems,
          { id, quantity: 1, type: type, cardData, removePreview: removePreviewCb },
        ];
      } else {
        return currItems.map(item => {
          if (item.id === id) {
            return {
              ...item,
              quantity: item.quantity + 1,
              type: type,
              cardData,
            };
          } else {
            return item;
          }
        });
      }
    });
  }

  function restoreCart(items: any[]) {
    setCartItems(items);
  }

  // Update the Cart
  function updateCart(cardData: any, fromOverlap: Boolean = false, cartItemId?: string) {
    let index = fromOverlap
      ? cartItems?.findIndex(item => item.id === cartItemId)
      : cartItems?.findIndex(item => item.id === cardData.id);
    let newCartItems = [...cartItems];
    newCartItems[index] = { ...newCartItems[index], cardData };

    setCartItems(newCartItems);
  }

  function updateCartItem(cartItem: CartItem) {
    let index = cartItems?.findIndex(item => item.id === cartItem.id);
    //creating new cart item array
    let newCartItems = [...cartItems];
    //creating new object cart item
    newCartItems[index] = { ...cartItem };
    setCartItems(newCartItems);
  }

  function updateWholeCart(cardData: any) {
    setCartItems(cardData);
  }

  // increaseCartQuantity: (id: any, type: string, cardData: any) => void;
  function AddPrimaryMergeItemCart(id: string, type: string, cardData: CartItemCardData) {
    // let newCartItems = [...cartItems];

    // cardData.overlapError = {
    //   noOverlap: true,
    // };

    //setting the primary item

    let overlapMap = { [id]: {} };

    let overlapResize: any = {};

    if (
      getPrimaryPolygonInterSectPercentage(cardData.coordinates, cardData.drawPolygonCoordinates) >
      80
    )
      overlapResize = { [id]: true };

    // cardData.startDate = cardData.startDate;
    cardData.lastDate = cardData.startDate;

    let newCardData = { ...cardData };
    multiProductCartItemRef.current = {
      id: `multi-${uuid()}`,
      quantity: 1,
      type: type,
      cardData: newCardData,
      //default overlap is unstitched
      overlap: MOSACIKING.UNSTITCHED,
      overlapData: [cardData],
      overlapMap,
      overlapError: { map: true, resize: overlapResize[id] ? true : false },
      overlapResize,
      // overlapId: [id],
    };
    //adding the merge item
    // this is to know which item is added in cart in sider page also it helps in deletion
    // mergeItemRef.current[id] = true;

    // disabling the src and bands for the secondary item
    // and add similar item according to the primary item
    disableSrcRef.current = cardData.source || '';
    disableBandsRef.current = cardData.source?.includes(K.LABELS.SOURCES.VANTOR)
      ? cardData.bandsLength
      : '';
    disableConstellationRef.current =
      (cardData.source?.includes(K.LABELS.SOURCES.VANTOR) ? cardData.constellation : '') || '';

    // newCartItems.push({ id: cardData.id, type, quantity: 0, cardData });
    const newCartItems = [...cartItems];
    //setting the state so it can render and updates the ref
    setCartItems(newCartItems);
    // setCartItems(cartItems => [...cartItems, newCartItem]);
  }

  function resetMultiProductCartItem() {
    mergeOverlapRef.current = false;
    disableSrcRef.current = '';
    disableBandsRef.current = '';
    disableConstellationRef.current = '';
    mergeOverlapPrimaryItemRef.current = false;
    multiProductCartItemRef.current = { id: '', quantity: 0, type: '', cardData: { id: '' } };
  }

  function confirmMergeItemCart() {
    const multiCartItem = multiProductCartItemRef.current;

    multiCartItem.overlapData?.forEach(cardData => {
      mergeItemRef.current[cardData.id] = true;
    });

    // multiCartItem.overlapMap =

    //resetting the refs
    resetMultiProductCartItem();
    setCartItems([...cartItems, multiCartItem]);
    openCart();

    // emitMixpanelEvents(MixpanelEventHub.HUB_EXPLORE_EXISTING_ADD_TO_CART_CLICK, {
    //             date_taken: mixPanelDateFormat(cardData?.startDate),
    //             source: cardData?.source,
    //             resolution: cardData?.resolution,
    //             price: cardData?.price,
    //             search_id: cardData?.searchId,
    //             result_id: cardData?.id,
    // offering_id :cardData?.originalData?.product?.configuration?.offering_id,
    // data_type: cardData?.originalData?.sensor_type,
    //           })

    emitMixpanelEvents(MixpanelEventHub.HUB_EXPLORE_EXISTING_MULTI_PRODUCT_ADD_TO_CART_CLICK, {
      date_taken: multiCartItem.overlapData?.map(value => mixPanelDateFormat(value.startDate)),
      source: multiCartItem.overlapData?.map(value => value.source),
      resolution: multiCartItem.overlapData?.map(value => value.resolution),
      price: multiCartItem.overlapData?.map(value => value.price),
      search_id: multiCartItem.overlapData?.at(0)?.searchId,
      result_id: multiCartItem.overlapData?.map(value => value.id),
    });
  }

  // async function ConfirmMergeItemCart(
  //   cardData1_Index: number,
  //   cardData2_Index: number,
  //   mergeType: MOSACIKING,
  //   coordinates: any
  // ) {
  //   let cardData1 = cartItems[cardData1_Index];
  //   let cardData2 = cartItems[cardData2_Index];

  //   const response = await calculateMergeItemPrice(coordinates, cardData1.cardData, mergeType);

  //   if (response.errors) {
  //     customToaster(`${response.errors[0]?.message}`);
  //     return;
  //   }

  //   let price = response?.data && response?.data?.max_cost;

  //   //add the merge item
  //   mergeItemRef.current[cardData2.id] = true;
  //   //remove the second item
  //   cartItems.splice(cardData2_Index, 1);

  //   let newCartItems = [...cartItems];
  //   //add the merge item details
  //   newCartItems[cardData1_Index] = {
  //     ...newCartItems[cardData1_Index],
  //     overlap: mergeType,
  //     overlapId: [cardData2.id],

  //     cardData: {
  //       ...newCartItems[cardData1_Index].cardData,
  //       price,
  //     },
  //   };

  //   setCartItems(newCartItems);
  // }

  async function AddSecondaryMergeItemCart(
    id: string,
    cardData: CartItemCardData,
    coordinates: any
  ) {
    // const enableSkySatOfferingId = posthog.isFeatureEnabled('4_April_Enable_SkySat_Offering_Id');
    const enableSkySatOfferingId = true;
    const firstItem = multiProductCartItemRef.current?.overlapData;
    let updatedCardData = firstItem ? firstItem[0] : multiProductCartItemRef.current.cardData;
    updatedCardData =
      updatedCardData?.source?.includes('SkySat') && !enableSkySatOfferingId
        ? { ...updatedCardData, archiveOfferingId: null }
        : updatedCardData;
    // eslint-disable-next-line require-use-server
    const response = await calculateMergeItemPrice(
      coordinates,
      updatedCardData,
      MOSACIKING.UNSTITCHED
    );

    if (response.errors) {
      customToaster(`${response.errors[0]?.message}`);
      return;
    }
    let price = response?.data && response?.data?.max_cost;

    const currentStartDateUnix = moment(multiProductCartItemRef.current.cardData.startDate).unix();
    const currentLastDateUnix = moment(multiProductCartItemRef.current.cardData.lastDate).unix();
    const cardDateUnix = moment(cardData.lastDate).unix();
    //compare time
    //start date
    if (cardDateUnix > currentStartDateUnix)
      multiProductCartItemRef.current.cardData.startDate = cardData.startDate;
    //end or last date
    if (cardDateUnix < currentLastDateUnix)
      multiProductCartItemRef.current.cardData.lastDate = cardData.startDate;

    //checking and creating the overlap map

    const cardDataPolygon = new Polygon({ rings: cardData.coordinates });

    //if no overlap exist create new
    if (!multiProductCartItemRef.current.overlapMap)
      multiProductCartItemRef.current.overlapMap = {};

    let overlapMap = multiProductCartItemRef.current.overlapMap;

    overlapMap[cardData.id] = {};
    multiProductCartItemRef.current.overlapData?.forEach(overlapCardData => {
      let overlap = geometryEngine.intersect(
        cardDataPolygon,
        new Polygon({ rings: overlapCardData.coordinates })
      );

      //if true make the entry in overlap map if not no entry needed
      if (overlap) {
        //if the object already exist append it otherwise create new
        overlapMap[cardData.id] = { ...overlapMap[cardData.id], [overlapCardData.id]: true };

        overlapMap[overlapCardData.id] = { ...overlapMap[overlapCardData.id], [cardData.id]: true };
      }
    });

    // add the merge item
    // this is to know which item is added in cart in sider page also it helps in deletion
    // mergeItemRef.current[id] = true;

    //adding the id
    multiProductCartItemRef.current.overlapData?.push(cardData);

    let overlapError = false;
    let resizeError =
      multiProductCartItemRef.current.overlapResize &&
      Object.keys(multiProductCartItemRef.current.overlapResize).length > 0
        ? true
        : false;
    //check for non overlaps error for primary item
    multiProductCartItemRef.current.overlapMap &&
      Object.keys(multiProductCartItemRef.current.overlapMap).forEach(value => {
        if (!multiProductCartItemRef.current.overlapMap) return;
        if (Object.keys(multiProductCartItemRef.current.overlapMap[value]).length === 0)
          overlapError = true;
      });

    //check for 80 overlap
    if (
      getPrimaryPolygonInterSectPercentage(cardData.coordinates, cardData.drawPolygonCoordinates) >
      80
    ) {
      resizeError = true;
      multiProductCartItemRef.current.overlapResize &&
        (multiProductCartItemRef.current.overlapResize[cardData.id] = true);
    }

    multiProductCartItemRef.current.overlapError = { map: overlapError, resize: resizeError };
    //updating the price
    multiProductCartItemRef.current.cardData.price = price;

    //primary cartitem is the latest one
    let newCartItems = [...cartItems];
    //setting the render the update the refs
    setCartItems(newCartItems);
  }

  //function for removing the cart item on deselection
  async function removeSelectionItemCart(id: string) {
    if (!multiProductCartItemRef.current.overlapData) return;
    const productIndex = multiProductCartItemRef.current.overlapData.findIndex(
      product => product.id
    );
    multiProductCartItemRef.current.overlapData.splice(productIndex, 1);

    //remove overlap data since the polygon is removed
    if (multiProductCartItemRef.current.overlapMap) {
      let overlapMap = multiProductCartItemRef.current.overlapMap;

      //deleting the overlap with polygon data
      overlapMap &&
        Object.keys(overlapMap[id]).forEach(value => {
          if (overlapMap[value][id]) delete overlapMap[value][id];
        });

      //deleting the main product
      delete overlapMap[id];
    }

    //if the overlap resize exist with this polygon delete it

    if (
      multiProductCartItemRef.current.overlapResize &&
      multiProductCartItemRef.current.overlapResize[id]
    )
      delete multiProductCartItemRef.current.overlapResize[id];

    // mergeItemRef.current[id] && delete mergeItemRef.current[id];

    let overlapError = false;
    //check for non overlaps error for primary item
    multiProductCartItemRef.current.overlapMap &&
      Object.keys(multiProductCartItemRef.current.overlapMap).forEach(value => {
        if (!multiProductCartItemRef.current.overlapMap) return;
        if (Object.keys(multiProductCartItemRef.current.overlapMap[value]).length === 0)
          overlapError = true;
      });

    multiProductCartItemRef.current.overlapError = {
      map: overlapError,
      resize:
        multiProductCartItemRef.current.overlapResize &&
        Object.keys(multiProductCartItemRef.current.overlapResize).length > 0
          ? true
          : false,
    };
    //if the last item is removed and length is zero
    if (multiProductCartItemRef.current.overlapData.length === 0) {
      resetMultiProductCartItem();
    }

    //setting the render the update the refs
    let newCartItem = [...cartItems];
    setCartItems(newCartItem);
  }

  function updateMultiProductItem(
    cartId: string,
    updateCartData: {
      overlapData: CartItemCardData[];
      overlapMap: any;
      overlapError: { map: boolean; resize: boolean };
    },
    addCartData?: CartItem
  ) {
    const cartItemIndex = cartItems.findIndex(item => item.id === cartId);

    if (cartItemIndex === -1) {
      return false;
    }
    cartItems[cartItemIndex] = {
      ...cartItems[cartItemIndex],
      ...updateCartData,
    };

    //if the length of overlap is 1 then convert to single product
    if (updateCartData.overlapData.length === 1) {
      cartItems[cartItemIndex] = {
        id: updateCartData.overlapData[0].id,
        type: orderType.EXISTING,
        cardData: updateCartData.overlapData[0],
        quantity: 1,
      };
    }

    let newCartItem = addCartData ? [...cartItems, ...[addCartData]] : [...cartItems];

    setCartItems(newCartItem);

    return true;
  }

  function resetCart(items?: any, dontResetTax?: any) {
    //empty the merge item
    mergeItemRef.current = {};
    //if there is any preview and highlighted item remove it all
    removeAndHighlightLayerCbRef.current && removeAndHighlightLayerCbRef.current();
    setCartItems(items ? items : []);
    if (!dontResetTax) {
      setTax(0);
    }
  }

  // Decrease the quanitity
  function decreaseCartQuantity(id: number) {
    setCartItems(currItems => {
      if (currItems.find(item => item.id === id)?.quantity === 1) {
        return currItems.filter(item => item.id !== id);
      } else {
        // Still don't know what is the quantity case
        return currItems.map(item => {
          if (item.id === id) {
            return { ...item, quantity: item.quantity - 1 };
          } else {
            return item;
          }
        });
      }
    });
  }

  // Remove an item from Cart
  function removeFromCart(id: number | string) {
    setCartItems(currItems => {
      return currItems.filter(item => {
        if (item.id === id) {
          //if there is preview exist for the item then remove it
          if (item.removePreview) item.removePreview();
          //removing the merge item if exists
          if (item?.overlap) {
            mergeOverlapRef.current = false;
            disableSrcRef.current = '';
            disableBandsRef.current = '';
            disableConstellationRef.current = '';
            mergeOverlapPrimaryItemRef.current = false;
            item.overlapData &&
              item.overlapData.forEach(value => {
                if (mergeItemRef.current[value.id]) delete mergeItemRef.current[value.id];
              });
          }

          return false;
        }

        return true;
      });
    });
  }

  return (
    <ShoppingCartContext.Provider
      value={{
        removeAndHighlightLayerCbRef,
        updateMultiProductItem,
        confirmMergeItemCart,
        multiProductCartItemRef,
        removeSelectionItemCart,
        openCreateAccountDrawer,
        setOpenCreateAccountDrawer,
        getItemQuantity,
        increaseCartQuantity,
        decreaseCartQuantity,
        updateCart,
        updateCartItem,
        updateWholeCart,
        feasibilityRequestData,
        removeFromCart,
        setFeasibilityRequestData,
        AddSecondaryMergeItemCart,
        openCart,
        closeCart,
        cartItems,
        openPreviewInfoPopup,
        setOpenPreviewInfoPopup,
        cartQuantity,
        AddPrimaryMergeItemCart,
        setAssuredTaskingDialog,
        assuredTaskingDialog,
        resetCart,
        isNew,
        setIsNew,
        mergeOverlapRef,
        archiveUiElementsRef,
        taskingUiElementsRef,
        mergeOverlapPrimaryItemRef,
        disableConstellationRef,
        disableSrcRef,
        disableBandsRef,
        drawer,
        aoiHeightWidth,
        setAoiHeightWidth,
        taskingMapLegends,
        setTaskingMapLegends,
        restoreCart,
        setDrawer,
        isOpen,
        setIsOpen,
        archiveFilters,
        setArchiveFilters,
        tax,
        setTax,
        orderNewDataType,
        orderNewIsGuaranteedCapture,
        feasibilityData,
        mergeItemRef,
        mixpanelData,
        tabValue,
        setMixpanelData,
        multiProduct,
        setMultiProduct,
      }}
    >
      {children}
    </ShoppingCartContext.Provider>
  );
}
